/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/message.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ArticleTeaser } from 'components/articleLink'
import { Search } from 'components/search'
import { CopyToClipboardUrl } from 'components/copyToClipboardUrl'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'

const ARTICLE_ID = '306707382831'

const State = {
  Loading: 0,
  Ready: 1,
  Error: 2
}

export function AppComponent() {
  const [readArticles, setReadArticles] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [requestState, setRequestState] = useState(State.Loading)
  const [teasers, setTeasers] = useState([])
  const [stories, setStories] = useState([])
  const [sectionsOrder, setSectionsOrder] = useState([])
  const sections = teasers.reduce(
    (result, item) => ({
      ...result,
      [item.section]: [...(result[item.section] || []), item]
    }),
    {}
  )

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768)
  }

  const handleTeaserClick = id => {
    const newReadArticles = [...new Set([...readArticles, +id])]
    setReadArticles(newReadArticles)
    localStorage.setItem(ARTICLE_ID, JSON.stringify(newReadArticles))
  }

  const handleArticleLoaded = articleData => {
    setStories(p => [...p, articleData])
  }

  useEffect(() => {
    if (localStorage.getItem(ARTICLE_ID)) {
      setReadArticles(JSON.parse(localStorage.getItem(ARTICLE_ID)))
    }

    fetch(
      `https://storage.googleapis.com/wch-interactive/question-verte/questions.json`
    ).then(res => {
      return res.json().then(json => {
        console.log(json)
        setTeasers(json.data.questions)
        setSectionsOrder(json.data.categories)
        setRequestState(State.Ready)
      })
    })

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <div className='ui vertical segment' style={{ paddingBottom: 0 }}>
        <div className='ui text center aligned container'>
          <img
            style={{
              width: '100%',
              maxWidth: '400px',
              mixBlendMode: 'multiply'
            }}
            src={require('../../images/illu-test-2.png')}
          />
        </div>
      </div>

      <header
        className='ui vertical  very fitted  segment'
        style={{ paddingTop: 0 }}
      >
        <div className='ui container'>
          <div className='ui header'>
            <h1>La question verte</h1>
            <h2>
              Nous répondons aux questions sur la thématique du climat que nous
              nous posons dans nos vies quotidiennes.
            </h2>
          </div>
          {stories.length > 0 && (
            <div className='ui ta authors'>
              Mis à jour le{' '}
              {new Date(
                stories.sort(
                  (a, b) => b.updateDate - a.updateDate
                )[0].updateDate
              ).toLocaleDateString('fr-CH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          )}
          {/* <div className='ui ta authors'>Le Matin Dimanche</div> */}
        </div>
      </header>

      {requestState !== State.Ready && (
        <div className='ui vertical very fitted  segment'>
          <div className='ui center aligned text container'>
            {requestState === State.Loading && (
              <div className='ui icon message'>
                <div className='content'>
                  <i
                    style={{ fontSize: '2em' }}
                    className='notched circle loading icon'
                  />
                  <div className='header'>Encore quelques secondes</div>
                  <p>Nous cherchons les questions pour vous.</p>
                </div>
              </div>
            )}
            {requestState === State.Error && (
              <div className='ui icon message'>
                <div className='content'>
                  <div className='header'>Les questions sont introuvables</div>
                  <p>Veuillez réessayer plus tard.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {requestState === State.Ready && (
        <>
          <div className='colored'>
            <div className='ui container'>
              <ArticleTeaser
                handleArticleLoaded={handleArticleLoaded}
                handleClick={handleTeaserClick}
                readArticles={readArticles}
                title='Dernières questions'
                inverted
                ids={teasers
                  .slice(teasers.length - (isMobile ? 5 : 4), teasers.length)
                  .map(t => +t.id)}
              />
            </div>
          </div>

          <div className='ui vertical segment'>
            <div className='ui container'>
              <div className='ui message' style={{ borderRadius: '83px' }}>
                <Search ids={teasers.map(t => +t.id)} />
              </div>
            </div>
          </div>

          <div className='ui container'>
            {sectionsOrder.map((section, index) => {
              return (
                <>
                  {index === 3 && (
                    <CopyToClipboardUrl
                      locale='fr'
                      title='Partager la Question Verte'
                      text='Partagez les questions avec vos amis et vos connaissances!'
                      url={window.location.href}
                      buttonColor='var(--stda-main-color)'
                    />
                  )}
                  {index === 1 && (
                    // picto mail
                    <FeedbackMessage
                      mailTo='interactif@tamedia.ch'
                      question="Vous vous posez d'autres questions?"
                      locale='fr'
                    />
                  )}
                  <ArticleTeaser
                    handleArticleLoaded={handleArticleLoaded}
                    handleClick={handleTeaserClick}
                    readArticles={readArticles}
                    sep
                    key={`category-${section.nom}`}
                    title={section.nom}
                    ids={sections[section.nom].map(s => +s.id)}
                  />
                </>
              )
            })}
          </div>
        </>
      )}

      <Footer
        mailTo='interactif@tamedia.ch'
        hidePortfolioTeaser
        locale='fr'
        articleId={ARTICLE_ID}
        credits={[
          [
            'Textes (Le Matin Dimanche)',
            [...new Set(stories.reduce((a, b) => [b.authors, ...a], []))]
              .map(a => {
                const items = a.split(' ')
                return {
                  firstName: items.slice(0, items.length - 1).join(' '),
                  lastName: items[items.length - 1]
                }
              })
              .sort((a, b) => a.lastName.localeCompare(b.lastName))
              .map(a => a.firstName + ' ' + a.lastName)
              .join(', ')
          ],
          ['Illustrations', 'Louiza'],
          ['Conception et réalisation', 'Mathieu Rudaz']
        ]}
      />
    </div>
  )
}
